import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Table, Button, Alert, Spinner, Modal, Form, Pagination } from "react-bootstrap";
import "../../../assets/css/emp_dashboard.css";
import { useNavigate } from "react-router-dom";
import LeftNavManagement from "../LeftNavManagement";
import AdminHeader from "../AdminHeader";

// Define base URL for your API
const API_BASE_URL = 'https://brainrock.in/brainrock/backend';

const ContactUsQuery = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const navigate = useNavigate();
  
  // Data state
  const [contactQueries, setContactQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Show 10 entries per page
  
  // State for selected contact
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Selection state for bulk delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // Responsive check
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setSidebarOpen(width >= 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Fetch contact queries on component mount
  useEffect(() => {
    const fetchContactQueries = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/contact-us/`, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch contact queries');
        }
        const result = await response.json();
        
        // The API returns data directly as an array
        if (Array.isArray(result)) {
          // Use the result directly as it's already an array
          setContactQueries(result);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching contact queries:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContactQueries();
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Format date for better readability
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to handle viewing contact details
  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setShowContactModal(true);
  };
  
  // Function to close modal
  const handleCloseModal = () => {
    setShowContactModal(false);
  };

  // Filter contacts based on search term (email and phone)
  const filteredContacts = useMemo(() => {
    if (!searchTerm) {
      return contactQueries;
    }
    
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return contactQueries.filter(contact =>
      contact.full_name?.toLowerCase().includes(lowercasedSearchTerm) ||
      contact.email?.toLowerCase().includes(lowercasedSearchTerm) ||
      contact.phone?.toLowerCase().includes(lowercasedSearchTerm) ||
      contact.subject?.toLowerCase().includes(lowercasedSearchTerm) ||
      contact.message?.toLowerCase().includes(lowercasedSearchTerm)
    );
  }, [contactQueries, searchTerm]);

  // Reset page on search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Get current page's contacts
  const paginatedContacts = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredContacts.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredContacts, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  
  // Handle page change
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedIds(paginatedContacts.map(contact => contact.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectContact = (id, checked) => {
    setSelectedIds(prev => 
      checked ? [...prev, id] : prev.filter(selectedId => selectedId !== id)
    );
    // Update selectAll state
    if (!checked) {
      setSelectAll(false);
    } else if (selectedIds.length + 1 === paginatedContacts.length) {
      setSelectAll(true);
    }
  };

  // Reset selection on page change
  useEffect(() => {
    setSelectedIds([]);
    setSelectAll(false);
  }, [currentPage]);

  // Single delete handler
  const handleSingleDelete = (contact) => {
    setContactToDelete(contact);
    setDeleteType('single');
    setShowDeleteModal(true);
    setDeleteError(null);
    setDeleteSuccess(false);
  };

  // Bulk delete handler
  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setDeleteType('bulk');
    setShowDeleteModal(true);
    setDeleteError(null);
    setDeleteSuccess(false);
  };

// Execute delete
  const executeDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      const idsToDelete = deleteType === 'single' && contactToDelete 
        ? [contactToDelete.id] 
        : selectedIds;
      
      const response = await fetch(`${API_BASE_URL}/api/contact-us/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ ids: idsToDelete }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete contact query');
      }
      
      setContactQueries(prev => prev.filter(q => !idsToDelete.includes(q.id)));
      
      setDeleteSuccess(true);
      setShowDeleteModal(false);
      setSelectedIds([]);
      setSelectAll(false);
      setContactToDelete(null);
    } catch (err) {
      setDeleteError(err.message);
      console.error('Error deleting contact query:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteType(null);
    setContactToDelete(null);
    setDeleteError(null);
    setDeleteSuccess(false);
  };

  return (
    <div className="dashboard-container">
      <LeftNavManagement
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isMobile={isMobile}
        isTablet={isTablet}
      />
      <div className="main-content">
        <AdminHeader toggleSidebar={toggleSidebar} />
        <Container fluid className="dashboard-body">
          <div className="br-box-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0">Contact Us Queries</h2>
              
              {/* Search functionality */}
              <div className="d-flex align-items-center">
                <span className="me-2">Search:</span>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, or phone"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control me-3"
                  style={{ width: '278px' }}
                />
              </div>
            </div>
            
            {loading && <div className="d-flex justify-content-center"><Spinner animation="border" /></div>}
            {error && <Alert variant="danger">Failed to load contact queries: {error}</Alert>}

            {!loading && !error && (
              <>
                {/* --- TABLE STRUCTURE --- */}
                <Row className="mt-3">
                  <div className="col-md-12">
                    <table className="temp-rwd-table">
                      <tbody>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </th>
                          <th>S.No</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Subject</th>
                          <th>Message</th>
                          <th>Date</th>
                          <th>Action</th>
                        </tr>
                        
                        {paginatedContacts.length > 0 ? (
                          paginatedContacts.map((query, index) => (
                            <tr key={query.id}>
                              <td data-th="Select" style={{ width: '40px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(query.id)}
                                  onChange={(e) => handleSelectContact(query.id, e.target.checked)}
                                />
                              </td>
                              <td data-th="S.No">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td data-th="Name">{query.full_name}</td>
                              <td data-th="Email">{query.email}</td>
                              <td data-th="Phone">{query.phone}</td>
                              <td data-th="Subject">{query.subject}</td>
                              <td data-th="Message" className="message-cell">
                                {query.message.length > 50 
                                  ? `${query.message.substring(0, 50)}...` 
                                  : query.message}
                              </td>
                              <td data-th="Date">{formatDate(query.created_at)}</td>
                              <td data-th="Action">
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  onClick={() => handleViewContact(query)}
                                  className="me-1"
                                >
                                  View
                                </Button>
                                <Button 
                                  variant="danger" 
                                  size="sm"
                                  onClick={() => handleSingleDelete(query)}
                                >
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center">
                              No contact queries found matching your search.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Row>
              
                {/* --- PAGINATION CONTROLS --- */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div>
                      {selectedIds.length > 0 && (
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={handleBulkDelete}
                          disabled={deleting}
                        >
                          Delete Selected ({selectedIds.length})
                        </Button>
                      )}
                    </div>
                    <div className="d-flex justify-content-center">
                      <Pagination>
                        <Pagination.Prev 
                          onClick={() => handlePageChange(currentPage - 1)} 
                          disabled={currentPage === 1}
                        />
                        {[...Array(totalPages).keys()].map(page => (
                          <Pagination.Item 
                            key={page + 1} 
                            active={page + 1 === currentPage}
                            onClick={() => handlePageChange(page + 1)}
                          >
                            {page + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next 
                          onClick={() => handlePageChange(currentPage + 1)} 
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Container>
      </div>
      
      {/* CONTACT DETAILS MODAL */}
      <Modal show={showContactModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Contact Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContact && (
            <Row>
              <Col md={6} className="mb-3">
                <h5>Name</h5>
                <p>{selectedContact.full_name}</p>
              </Col>
              <Col md={6} className="mb-3">
                <h5>Email</h5>
                <p>{selectedContact.email}</p>
              </Col>
              <Col md={6} className="mb-3">
                <h5>Phone</h5>
                <p>{selectedContact.phone}</p>
              </Col>
              <Col md={6} className="mb-3">
                <h5>Subject</h5>
                <p>{selectedContact.subject}</p>
              </Col>
              <Col md={12} className="mb-3">
                <h5>Message</h5>
                <p>{selectedContact.message}</p>
              </Col>
              <Col md={6} className="mb-3">
                <h5>Date</h5>
                <p>{formatDate(selectedContact.created_at)}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* DELETE CONFIRMATION MODAL */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{deleteType === 'bulk' ? 'Delete Multiple Queries' : 'Delete Contact Query'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteSuccess ? (
            <Alert variant="success">
              {deleteType === 'bulk' 
                ? `Successfully deleted ${selectedIds.length} contact queries.` 
                : 'Contact query deleted successfully.'}
            </Alert>
          ) : (
            <>
              {deleteError && <Alert variant="danger">{deleteError}</Alert>}
              <p>
                {deleteType === 'bulk' 
                  ? `Are you sure you want to delete ${selectedIds.length} selected contact query${selectedIds.length > 1 ? 'ies' : ''}? This action cannot be undone.`
                  : `Are you sure you want to delete the contact query from "${contactToDelete?.full_name}" (${contactToDelete?.email})? This action cannot be undone.`}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {deleteSuccess ? (
            <Button variant="primary" onClick={handleCloseDeleteModal}>
              OK
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleting}>
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={executeDelete} 
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContactUsQuery;